export interface PartnerConfig {
  label: string;
  /**
   * Do not ask this partner's customers for an IBAN: they arrange payment
   * details with the partner, so the mandate is signed without one.
   *
   * A commercial arrangement per partner, not something derivable from the
   * referral, which is why it lives here. Absent means we collect an IBAN, so a
   * partner we have no entry for is never silently let through without one.
   */
  hideIban?: boolean;
}

/**
 * Referral partners, keyed by the `partner` value stored on a profile.
 *
 * Presentation only: a label. Artwork lives in `brand-logo-src.ts`. No flow depends
 * on an entry existing — the mandate form keys off the data a partner sent, not
 * off who they are — so a new partner works before it is listed here and only
 * needs adding to be named in the UI.
 */
export const PARTNERS = {
  centraalbeheer: {
    label: "Centraal Beheer",
  },
  zonneplan: {
    label: "Zonneplan",
    // Only Zonneplan: their customers settle payment details with them, so the
    // mandate is signed without an IBAN.
    hideIban: true,
  },
  keuze_nl: {
    label: "keuze.nl",
  },
} as const satisfies Record<string, PartnerConfig>;

export type PartnerKey = keyof typeof PARTNERS;

/**
 * Every stored `partner` value, for validating a partner that arrives in a URL.
 *
 * Derived rather than written out a second time: a partner link carries
 * `?partner=` straight from the onboarding URL the API builds, so a list that
 * omits one silently breaks that partner's entire entry flow.
 */
export const PARTNER_KEYS = Object.keys(PARTNERS) as [PartnerKey, ...PartnerKey[]];

export const isKnownPartner = (value: unknown): value is PartnerKey =>
  typeof value === "string" && value in PARTNERS;

/** Config for a stored `partner` value, or null when it is unset or unknown. */
export function partnerConfig(partner?: string | null): PartnerConfig | null {
  return isKnownPartner(partner) ? PARTNERS[partner] : null;
}

/**
 * Name to show the customer for a partner. Falls back to a generic phrase so a
 * partner we have no entry for still reads correctly in a sentence.
 */
export function partnerLabel(partner?: string | null): string {
  return partnerConfig(partner)?.label ?? "onze partner";
}

/**
 * Whether the mandate form should skip the IBAN field for this partner.
 *
 * Keeps the form itself partner-agnostic: it renders on this boolean and never
 * looks at who referred the customer.
 */
export function partnerHidesIban(partner?: string | null): boolean {
  return partnerConfig(partner)?.hideIban ?? false;
}
